import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getCommentLikes, likeComment, unlikeComment } from '../../../firebase/solutionReasonCommentLikesApi.js';

// Async thunks
export const fetchCommentLikes = createAsyncThunk(
  'solutionView/commentLikes/fetch',
  async ({ commentId, userId }) => {
    return await getCommentLikes(commentId, userId);
  }
);

export const addCommentLike = createAsyncThunk(
  'solutionView/commentLikes/like',
  async ({ commentId, userId }) => {
    return await likeComment(commentId, userId);
  }
);

export const removeCommentLike = createAsyncThunk(
  'solutionView/commentLikes/unlike',
  async ({ commentId, userId }) => {
    return await unlikeComment(commentId, userId);
  }
);

const commentLikesSlice = createSlice({
  name: 'solutionViewReasonCommentLikes',
  initialState: {
    likesByCommentId: {} // { [commentId]: { likes: number, userLiked: boolean, loading: false, error: null } }
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommentLikes.pending, (state, action) => {
        const { commentId } = action.meta.arg;
        state.likesByCommentId[commentId] = state.likesByCommentId[commentId] || {};
        state.likesByCommentId[commentId].loading = true;
        state.likesByCommentId[commentId].error = null;
      })
      .addCase(fetchCommentLikes.fulfilled, (state, action) => {
        const { commentId, likes, userLiked } = action.payload;
        state.likesByCommentId[commentId] = {
          likes,
          userLiked,
          loading: false,
          error: null
        };
      })
      .addCase(fetchCommentLikes.rejected, (state, action) => {
        const { commentId } = action.meta.arg;
        state.likesByCommentId[commentId] = state.likesByCommentId[commentId] || {};
        state.likesByCommentId[commentId].loading = false;
        state.likesByCommentId[commentId].error = action.error.message;
      })
      // Optimistic like
      .addCase(addCommentLike.pending, (state, action) => {
        const { commentId } = action.meta.arg;
        state.likesByCommentId[commentId] = state.likesByCommentId[commentId] || { likes: 0, userLiked: false };
        const entry = state.likesByCommentId[commentId];
        if (!entry.userLiked) {
          entry.userLiked = true;
          entry.likes = (entry.likes || 0) + 1;
        }
        entry.loading = true;
        entry.error = null;
      })
      .addCase(addCommentLike.fulfilled, (state, action) => {
        const { commentId, likes, userLiked } = action.payload;
        state.likesByCommentId[commentId] = {
          likes,
          userLiked,
          loading: false,
          error: null
        };
      })
      .addCase(addCommentLike.rejected, (state, action) => {
        const { commentId } = action.meta.arg;
        state.likesByCommentId[commentId] = state.likesByCommentId[commentId] || { likes: 0, userLiked: false };
        const entry = state.likesByCommentId[commentId];
        // Roll back optimistic like
        if (entry.userLiked) {
          entry.userLiked = false;
          entry.likes = Math.max(0, (entry.likes || 1) - 1);
        }
        entry.loading = false;
        entry.error = action.error.message;
      })
      // Optimistic unlike
      .addCase(removeCommentLike.pending, (state, action) => {
        const { commentId } = action.meta.arg;
        state.likesByCommentId[commentId] = state.likesByCommentId[commentId] || { likes: 0, userLiked: false };
        const entry = state.likesByCommentId[commentId];
        if (entry.userLiked) {
          entry.userLiked = false;
          entry.likes = Math.max(0, (entry.likes || 1) - 1);
        }
        entry.loading = true;
        entry.error = null;
      })
      .addCase(removeCommentLike.fulfilled, (state, action) => {
        const { commentId, likes, userLiked } = action.payload;
        state.likesByCommentId[commentId] = {
          likes,
          userLiked,
          loading: false,
          error: null
        };
      })
      .addCase(removeCommentLike.rejected, (state, action) => {
        const { commentId } = action.meta.arg;
        state.likesByCommentId[commentId] = state.likesByCommentId[commentId] || { likes: 0, userLiked: false };
        const entry = state.likesByCommentId[commentId];
        // Roll back optimistic unlike by re-liking
        if (!entry.userLiked) {
          entry.userLiked = true;
          entry.likes = (entry.likes || 0) + 1;
        }
        entry.loading = false;
        entry.error = action.error.message;
      });
  }
});

export default commentLikesSlice.reducer;
